import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    OnDestroy,
    OnInit,
    ChangeDetectionStrategy,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import {
    LicenseFeatureService,
    MiscApiService,
    SafeModeService,
    SidebarNotificationService,
    SignalrService,
    SuggestionPendingCountService,
    KpiAnnualTransitionPendingCountService,
} from '@masar/core/services';
import { messageTypeList } from '@masar/common/misc/message-type-list';
import { NavItem } from '@masar/shared/components/sidebar/types';
import { TranslateService } from '@ngx-translate/core';
import { uuid } from '@masar/common/utils';

@Component({
    selector: 'app-sidebar',
    templateUrl: './sidebar.component.html',
    styleUrls: ['./sidebar.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class SidebarComponent implements OnDestroy, OnInit, AfterViewInit {
    @Input() public isSidebarCollapsed: boolean;
    @Input() public navItems: NavItem[];
    @Input() public displayMode: 'dark' | 'light' = 'light';
    @Input() public enabledFilter = false;
    @Input() public scrollToItemOnNavigation = true;

    public uuid: string = `sidebar-${uuid()}`;

    public filteredItems: NavItem[];

    // Used as bases for the item's children, since
    // during filtering the children property will
    // be overwritten with the filtered children.
    // When we perform the search again we need to filter
    // items' children based on the original children list.
    // However, the children list will have been overwritten
    // by previous filtering.
    private itemToChildren: Map<NavItem, NavItem[]>;

    // Performance optimization: Cache flattened items and their paths
    private flattenedItems: Array<{ item: NavItem; linkPath: string }> = [];
    private pathToItemMap: Map<string, NavItem> = new Map();

    private unsubscribeAll = new Subject<void>();

    public constructor(
        public miscApiService: MiscApiService,
        public safeModeService: SafeModeService,
        public licenseFeatureService: LicenseFeatureService,
        private readonly sidebarNotificationService: SidebarNotificationService,
        private readonly suggestionPendingCountService: SuggestionPendingCountService,
        private readonly kpiAnnualTransitionPendingCountService: KpiAnnualTransitionPendingCountService,
        private readonly signalrService: SignalrService,
        private readonly router: Router,
        private readonly translateService: TranslateService,
        private readonly changeDetectorRef: ChangeDetectorRef,
        private readonly elementRef: ElementRef
    ) {}

    public ngOnInit(): void {
        this.hashItemsChildren();
        this.buildNavigationCache();
        this.translateTitles();
        this.filterItems('');
        this.refreshMyTasksCount();
        this.refreshSuggestionPendingCounts();
        this.initializeKpiAnnualTransitionPendingCount();

        // Because we are listening inside the ngOnInit, the first router
        // event won't be fired. We need to check before listening to account
        // for the first refresh.
        setTimeout(() => this.updateActiveStatus());

        this.router.events
            .pipe(takeUntil(this.unsubscribeAll))
            .subscribe(event => {
                if (!(event instanceof NavigationEnd)) return;

                this.updateActiveStatus();
            });

        this.signalrService
            .messages()
            .pipe(
                takeUntil(this.unsubscribeAll),
                filter(
                    x =>
                        x.type ===
                        messageTypeList.myTasksNotificationCountUpdate
                )
            )
            .subscribe(() => this.refreshMyTasksCount());
    }

    public ngAfterViewInit(): void {
        this.scrollIntoActiveLink();
    }

    public ngOnDestroy(): void {
        this.unsubscribeAll.next();
        this.unsubscribeAll.complete();
    }

    public toggleExpandableItem(item: NavItem): void {
        // Look for all items that should
        // not be closed by the toggle, basically
        // all of the upper level parents.
        const parents = [];
        const traverser = (items: NavItem[]): boolean => {
            const foundItem = items.find(x => {
                const children = this.itemToChildren.get(x);
                return (
                    x === item ||
                    ((x.type === 'expandable' || x.type == 'section') &&
                        children &&
                        children.length > 0 &&
                        traverser(children))
                );
            });

            if (!foundItem) return false;

            parents.push(foundItem);
            return true;
        };
        traverser(this.navItems);

        // Close all items that are not in the path.
        const closer = (items: NavItem[]): void => {
            items.forEach(x => {
                if (!parents.includes(x)) {
                    x.isOpen = false;
                }

                const children = this.itemToChildren.get(x);

                if (
                    (x.type === 'expandable' || x.type === 'section') &&
                    children &&
                    children.length > 0
                ) {
                    closer(children);
                }
            });
        };
        closer(this.navItems);

        // Toggle the item.
        item.isOpen = !item.isOpen;
    }

    public hasAnyFeature(featureIds: string[]): boolean {
        return this.licenseFeatureService.hasAnyFeature(featureIds);
    }

    public hasFeatureVisibleChildren(item: NavItem): boolean {
        if (!item.children || item.children.length === 0) return true;
        return item.children.some(child =>
            this.hasAnyFeature(child.featureIds)
        );
    }

    public filterItems(keyword: string): void {
        const deepFilter = (items: NavItem[]): NavItem[] => {
            return items.filter(item => {
                switch (item.type) {
                    case 'item':
                        return item.title.includes(keyword);
                    case 'expandable':
                    case 'section':
                        const children = this.itemToChildren.get(item);
                        if (!children || children.length === 0) return false;
                        item.children = deepFilter(children);
                        return item.children.length > 0;
                }
            });
        };

        this.filteredItems = deepFilter(this.navItems);

        // Rebuild cache after filtering to ensure consistency
        this.buildNavigationCache();
    }

    private refreshMyTasksCount(): void {
        if (!this.licenseFeatureService.hasFeature('tournaments')) return;

        this.sidebarNotificationService.getMyTasksCount().subscribe(count => {
            const section = this.navItems.find(
                x => x.tag === 'excellence_management'
            );

            if (!section) return;

            const expandable = this.itemToChildren
                .get(section)
                .find(x => x.tag === 'excellence_tournaments');

            if (!expandable) return;

            expandable.badgeCount = count;

            const myTasksItem = this.itemToChildren
                .get(expandable)
                .find(x => x.tag === 'my_tasks');

            if (!myTasksItem) return;

            myTasksItem.badgeCount = count;
        });
    }

    private refreshSuggestionPendingCounts(): void {
        this.suggestionPendingCountService.getCounts().subscribe(counts => {
            this.setBadgeCount(
                'suggestion_pending_initial_review',
                counts.initialReview
            );
            this.setBadgeCount(
                'suggestion_pending_lead_coordinator_decision',
                counts.leadCoordinatorDecision
            );
            this.setBadgeCount(
                'suggestion_pending_committee_evaluation',
                counts.committeeEvaluation
            );
            this.setBadgeCount(
                'suggestion_pending_organizational_unit_evaluation',
                counts.organizationalUnitEvaluation
            );
            this.setBadgeCount(
                'suggestion_pending_execution',
                counts.execution
            );
            this.setBadgeCount(
                'suggestion_pending_planned_execution',
                counts.plannedExecution
            );
            this.setBadgeCount('suggestion_pending_appealed', counts.appealed);
            this.changeDetectorRef.detectChanges();
        });
    }

    private initializeKpiAnnualTransitionPendingCount(): void {
        if (!this.licenseFeatureService.hasFeature('kpis')) return;

        this.kpiAnnualTransitionPendingCountService.pendingCount$
            .pipe(takeUntil(this.unsubscribeAll))
            .subscribe(count => {
                const item = this.findNavItemByTag('kpi_annual_transition');
                if (item) item.badgeCount = count;
            });

        this.kpiAnnualTransitionPendingCountService.refresh();
    }

    private findNavItemByTag(tag: string): NavItem | null {
        const search = (items: NavItem[]): NavItem | null => {
            for (const item of items) {
                if (item.tag === tag) return item;

                const children = this.itemToChildren.get(item);
                if (children) {
                    const found = search(children);
                    if (found) return found;
                }
            }

            return null;
        };

        return search(this.navItems);
    }

    private scrollIntoActiveLink(): void {
        if (!this.scrollToItemOnNavigation) return;

        const activeElement = this.elementRef.nativeElement.querySelector(
            `#${this.uuid} .nav-item-link.active`
        );

        activeElement?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest',
        });
    }

    private translateTitles(): void {
        const deepTranslate = (items: NavItem[]): void => {
            items.forEach(item => {
                item.title = this.translateService.instant(item.title);

                const children = this.itemToChildren.get(item);

                if (children && children.length > 0) {
                    deepTranslate(children);
                }
            });
        };

        deepTranslate(this.navItems);
    }

    private updateActiveStatus(): void {
        // Reset all active states efficiently
        this.resetActiveStates(this.navItems);

        const currentPath = this.router.url.split('?')[0];

        // Find the best matching item using cached data
        const bestMatch = this.findBestMatchingItem(currentPath);

        if (bestMatch) {
            bestMatch.isActive = true;
            // Open parent sections/expandable for the active item
            this.openParentSections(bestMatch);
        }

        this.changeDetectorRef.detectChanges();
        this.scrollIntoActiveLink();
    }

    private resetActiveStates(items: NavItem[]): void {
        items.forEach(x => {
            x.isActive = false;
            x.isOpen = false;
            const children = this.itemToChildren.get(x);
            if (children) this.resetActiveStates(children);
        });
    }

    private findBestMatchingItem(currentPath: string): NavItem | null {
        // Check for exact match first using the path map
        const exactMatch = this.pathToItemMap.get(currentPath);
        if (exactMatch) {
            return exactMatch;
        }

        // Find parent matches using cached flattened items
        const parentMatches = this.flattenedItems
            .filter(
                ({ linkPath }) =>
                    linkPath !== '/' &&
                    linkPath !== '' &&
                    currentPath.startsWith(linkPath + '/')
            )
            .sort((a, b) => b.linkPath.length - a.linkPath.length); // Longer paths first

        return parentMatches.length > 0 ? parentMatches[0].item : null;
    }

    private openParentSections(activeItem: NavItem): void {
        const currentPath = this.router.url.split('?')[0];
        const segments = currentPath === '/' ? [''] : currentPath.split('/');

        const findAndOpenParents = (items: NavItem[]): boolean => {
            return items.some(item => {
                const children = this.itemToChildren.get(item);

                if (item.type === 'item') {
                    return item === activeItem;
                }

                if (children && children.length > 0) {
                    const hasActiveChild = findAndOpenParents(children);
                    if (hasActiveChild) {
                        item.isOpen = true;
                        return true;
                    }

                    // Additional logic for opening based on URL segments
                    if (
                        segments.length > 1 &&
                        children[0]?.link?.[1] === segments[1]
                    ) {
                        item.isOpen = true;
                        return true;
                    }
                }

                return false;
            });
        };

        findAndOpenParents(this.navItems);
    }

    private hashItemsChildren(): void {
        this.itemToChildren = new Map<NavItem, NavItem[]>();
        const traverser = (items: NavItem[]): void => {
            items.forEach(item => {
                if (!item.children || item.children.length === 0) return;
                this.itemToChildren.set(item, item.children);
                traverser(item.children);
            });
        };
        traverser(this.navItems);
    }

    private buildNavigationCache(): void {
        this.flattenedItems = [];
        this.pathToItemMap.clear();

        const collectItems = (items: NavItem[]): void => {
            items.forEach(item => {
                if (item.type === 'item') {
                    const linkPath = item.link
                        ? '/' + item.link.filter(Boolean).join('/')
                        : '';

                    this.flattenedItems.push({ item, linkPath });
                    this.pathToItemMap.set(linkPath, item);
                }

                const children = this.itemToChildren.get(item);
                if (children && children.length > 0) {
                    collectItems(children);
                }
            });
        };

        collectItems(this.navItems);
    }

    private setBadgeCount(tag: string, count: number): void {
        const item = this.findItemByTag(this.navItems, tag);

        if (!item) {
            return;
        }

        item.badgeCount = count;
    }

    private findItemByTag(items: NavItem[], tag: string): NavItem | null {
        for (const item of items) {
            if (item.tag === tag) {
                return item;
            }

            const children = this.itemToChildren.get(item) || item.children;
            if (children?.length) {
                const nested = this.findItemByTag(children, tag);
                if (nested) {
                    return nested;
                }
            }
        }

        return null;
    }
}
